// Copyright © 2025 CATTLEytics Inc.
import React, { useCallback } from 'react';
import { Form } from 'react-bootstrap';

interface Props<ValueTy = string> {
  children?: React.ReactNode;
  label: string;
  name: string;
  onChange: (name: string, value: ValueTy) => void;
  value: ValueTy;
}

export const TextField: React.FC<Props> = ({ label, name, onChange, value, children }) => {
  const handleChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      onChange(name, ev.target.value);
    },
    [onChange, name],
  );

  const id = `text-field-${name}`;

  return (
    <Form.Group className="form-group mt-3 mb-1">
      <Form.Label htmlFor={id}>{label}</Form.Label>
      <Form.Control id={id} name={name} onChange={handleChange} type="text" value={value} />
      {children}
    </Form.Group>
  );
};

interface NumberProps extends Props<number | null> {
  hideSpinner?: boolean;
  max?: number;
  min?: number;
  step?: number;
}

export const NumberField: React.FC<NumberProps> = ({
  label,
  name,
  onChange,
  value,
  children,
  hideSpinner,
  max,
  min,
  step,
}) => {
  const handleChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      if (ev.target.value === '') {
        onChange(name, null);
        return;
      }
      onChange(name, Number(ev.target.value));
    },
    [onChange, name],
  );

  const id = `number-field-${name}`;

  return (
    <Form.Group className="form-group mt-3 mb-1">
      <Form.Label htmlFor={id}>{label}</Form.Label>
      <Form.Control
        className={hideSpinner ? 'nospin' : undefined}
        id={id}
        max={max}
        min={min}
        name={name}
        onChange={handleChange}
        step={step}
        type="number"
        value={typeof value === 'number' ? value : ''}
      />
      {children}
    </Form.Group>
  );
};

interface SelectProps<OptionTy> extends Props<{ key: string; value: OptionTy } | null> {
  formatOption?: (key: string, value: OptionTy) => string;
  options: Array<{
    key: string;
    value: OptionTy;
  }>;
}

export const SelectField = <OptionTy,>({
  label,
  name,
  onChange,
  value,
  children,
  options,
  formatOption,
}: SelectProps<OptionTy>): JSX.Element => {
  const handleChange = useCallback(
    (ev: React.ChangeEvent<HTMLSelectElement>) => {
      const key = ev.target.value;
      const selected = options.find((opt) => opt.key === key) || null;
      onChange(name, selected);
    },
    [onChange, name, options],
  );

  const id = `select-field-${name}`;

  return (
    <Form.Group className="form-group mt-3 mb-1">
      <Form.Label htmlFor={id}>{label}</Form.Label>
      <Form.Select
        disabled={options.length < 1}
        id={id}
        name={name}
        onChange={handleChange}
        value={value?.key}
      >
        {value ? null : <option value="">{''}</option>}

        {options.map(({ key, value }) => (
          <option key={key} value={key}>
            {formatOption ? formatOption(key, value) : value}
          </option>
        ))}
      </Form.Select>
      {children}
    </Form.Group>
  );
};
